import React, { useEffect, useState } from 'react';
import { UserPlus, Shield, Ban, CheckCircle, Trash2, Edit2, X, Loader2, Mail, MapPin, RefreshCw, Users as UsersIcon } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usersApi, teamsApi, UserRecord, UserRegionRow } from '../services/creatorApi';
import { toast } from 'sonner';
import { CreateUserModal } from '../components/CreateUserModal';
import { ConfirmModal } from '../components/ConfirmModal';
export function Users() {
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const [allTeamUsers, setAllTeamUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'Viewer' as UserRecord['Role'],
    regions: [] as string[],
    status: 'Active' as UserRecord['Status']
  });
  const canManageUsers = currentUser.Role === 'Super Admin' || currentUser.Role === 'Team Manager';
  const loadData = async () => {
    setLoading(true);
    try {
      // Always fetch users for the current team
      const fetchedUsers = await usersApi.findByTeam(currentWorkspace.name);
      setAllTeamUsers(fetchedUsers);
      // Fetch Team Regions
      const team = await teamsApi.findByTeamName(currentWorkspace.name);
      if (team?.Team_Regions) {
        setAvailableRegions(team.Team_Regions.map(r => r.Region_Name));
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, [currentWorkspace.name]);
  // Filter by region if a sub-region is selected in the header scope
  const filteredUsers = currentSubRegion ? allTeamUsers.filter(user => {
    // Show users who have no regions (access to all) OR have the selected region
    if (!user.User_Regions || user.User_Regions.length === 0) return true;
    return user.User_Regions.some(r => r.Region_Name === currentSubRegion);
  }) : allTeamUsers;
  const handleOpenModal = (user?: UserRecord) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.User_Name,
        email: user.Email,
        role: user.Role,
        regions: user.User_Regions?.map(r => r.Region_Name) || [],
        status: user.Status
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        role: 'Viewer',
        regions: [],
        status: 'Active'
      });
    }
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Name and Email are required');
      return;
    }
    setIsSaving(true);
    try {
      // Preserve existing subform row IDs for regions that already exist
      const existingRegions = editingUser?.User_Regions || [];
      const regionRows: UserRegionRow[] = formData.regions.map(r => {
        const existingRow = existingRegions.find(er => er.Region_Name === r);
        return {
          ...(existingRow?.ID ? {
            ID: existingRow.ID
          } : {}),
          Region_Name: r
        };
      });
      const payload = {
        User_Name: formData.name,
        Email: formData.email,
        Role: formData.role,
        Team: currentWorkspace.name,
        Status: formData.status,
        User_Regions: regionRows
      };
      if (editingUser) {
        await usersApi.update(editingUser.ID, payload);
        toast.success('User updated successfully');
      } else {
        await usersApi.create({
          ...payload,
          Invited_By: currentUser.Email || currentUser.email || ''
        });
        toast.success('User invited successfully');
      }
      handleCloseModal();
      loadData();
    } catch (err) {
      console.error('Failed to save user:', err);
      toast.error('Failed to save user');
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = (user: UserRecord) => {
    setUserToDelete(user);
  };
  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await usersApi.delete(userToDelete.ID);
      toast.success('User deleted');
      loadData();
    } catch (err) {
      console.error('Failed to delete user:', err);
      toast.error('Failed to delete user');
    }
    setUserToDelete(null);
  };
  const handleToggleStatus = async (user: UserRecord) => {
    const newStatus = user.Status === 'Active' ? 'Suspended' : 'Active';
    try {
      await usersApi.update(user.ID, {
        Status: newStatus
      });
      toast.success(`User ${newStatus === 'Active' ? 'activated' : 'suspended'}`);
      loadData();
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update user status');
    }
  };
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'Super Admin':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Team Manager':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Operator':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-6855">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-6856" />
      </div>;
  }
  return <div className="space-y-6" data-id="element-6857">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-6858">
        <div className="min-w-0" data-id="element-6859">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6860">Team Users</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6861">
            {currentSubRegion ? `Users with access to ${currentSubRegion} in ${currentWorkspace.name}.` : `Manage users for ${currentWorkspace.name}.`}
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-6862">
          <button onClick={loadData} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-6863">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-6864" />
          </button>
          {canManageUsers && <button onClick={() => handleOpenModal()} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0 shadow-sm transition-colors" data-id="element-6865">
              <UserPlus className="h-4 w-4 flex-shrink-0" data-id="element-6866" />
              <span data-id="element-6867">Invite User</span>
            </button>}
        </div>
      </div>

      {/* Summary bar */}
      {currentSubRegion && <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm" data-id="element-6868">
          <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0" data-id="element-6869" />
          <span className="text-blue-800" data-id="element-6870">
            Showing <strong data-id="element-6871">{filteredUsers.length}</strong> user
            {filteredUsers.length !== 1 ? 's' : ''} with access to{' '}
            <strong data-id="element-6872">{currentSubRegion}</strong>
            {allTeamUsers.length !== filteredUsers.length && <span className="text-blue-600 ml-1" data-id="element-6873">
                ({allTeamUsers.length} total in team)
              </span>}
          </span>
        </div>}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm" data-id="element-6874">
        {filteredUsers.length === 0 ? <div className="p-12 text-center" data-id="element-6875">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6876" />
            <h3 className="mt-4 text-lg font-medium text-gray-900" data-id="element-6877">
              {currentSubRegion ? `No users found for ${currentSubRegion}` : 'No users found'}
            </h3>
            <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto" data-id="element-6878">
              {currentSubRegion ? `No team members have access to the ${currentSubRegion} region. Invite a user or update an existing user's region access.` : 'Invite team members to collaborate on this workspace.'}
            </p>
            {canManageUsers && <button onClick={() => handleOpenModal()} className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-6879">
                <UserPlus className="h-4 w-4" data-id="element-6880" />
                Invite User
              </button>}
          </div> : <div className="overflow-x-auto" data-id="element-6881">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-6882">
              <thead className="bg-gray-50" data-id="element-6883">
                <tr data-id="element-6884">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6885">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6886">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6887">
                    Regions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6888">
                    Status
                  </th>
                  {canManageUsers && <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6889">
                        Reset Code
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-6890">
                        Actions
                      </th>
                    </>}
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200" data-id="element-6891">
                {filteredUsers.map(user => <tr key={user.ID} className="hover:bg-gray-50 transition-colors" data-id="element-6892">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6893">
                      <div className="flex items-center" data-id="element-6894">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-sm font-medium text-white mr-3 flex-shrink-0 shadow-sm" data-id="element-6895">
                          {user.User_Name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0" data-id="element-6896">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-6897">
                            {user.User_Name}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-[200px] flex items-center gap-1" data-id="element-6898">
                            <Mail className="h-3 w-3" data-id="element-6899" />
                            {user.Email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6900">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.Role)}`} data-id="element-6901">
                        <Shield className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-6902" />
                        {user.Role}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-6903">
                      <div className="flex flex-wrap gap-1 max-w-[200px]" data-id="element-6904">
                        {user.User_Regions && user.User_Regions.length > 0 ? user.User_Regions.map((r, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-6905">
                              {r.Region_Name}
                            </span>) : <span className="text-xs text-gray-400 italic" data-id="element-6906">
                            All Regions
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6907">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${user.Status === 'Active' ? 'bg-green-50 text-green-700 border-green-200' : user.Status === 'Suspended' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`} data-id="element-6908">
                        {user.Status === 'Active' && <CheckCircle className="h-3 w-3 mr-1" data-id="element-6909" />}
                        {user.Status === 'Suspended' && <Ban className="h-3 w-3 mr-1" data-id="element-6910" />}
                        {user.Status === 'Invited' && <Mail className="h-3 w-3 mr-1" data-id="element-6911" />}
                        {user.Status}
                      </span>
                    </td>

                    {canManageUsers && <>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-6912">
                          {user.Reset_Token && user.Reset_Token_Expires && new Date(user.Reset_Token_Expires) > new Date() ? <div className="flex items-center gap-2" data-id="element-6913">
                              <span className="font-mono text-sm font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200" data-id="element-6914">
                                {user.Reset_Token}
                              </span>
                              <span className="text-xs text-gray-400" data-id="element-6915">
                                Expires{' '}
                                {new Date(user.Reset_Token_Expires).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                              </span>
                            </div> : <span className="text-xs text-gray-400 italic" data-id="element-6916">
                              None
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6917">
                          <div className="flex items-center justify-end gap-2" data-id="element-6918">
                            <button onClick={() => handleOpenModal(user)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Edit User" data-id="element-6919">
                              <Edit2 className="h-4 w-4" data-id="element-6920" />
                            </button>
                            {user.Status === 'Active' ? <button onClick={() => handleToggleStatus(user)} className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded transition-colors" title="Suspend User" data-id="element-6921">
                                <Ban className="h-4 w-4" data-id="element-6922" />
                              </button> : <button onClick={() => handleToggleStatus(user)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors" title="Activate User" data-id="element-6923">
                                <CheckCircle className="h-4 w-4" data-id="element-6924" />
                              </button>}
                            <button onClick={() => handleDelete(user)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete User" data-id="element-6925">
                              <Trash2 className="h-4 w-4" data-id="element-6926" />
                            </button>
                          </div>
                        </td>
                      </>}
                  </tr>)}
              </tbody>
            </table>{' '}
          </div>}
      </div>

      {/* Create/Edit User Modal */}
      {isModalOpen && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6927">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6928">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal} data-id="element-6929" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden transform transition-all" data-id="element-6930">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-6931">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-6932">
                  {editingUser ? 'Edit User' : 'Invite New User'}
                </h3>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-500 transition-colors" data-id="element-6933">
                  <X className="h-5 w-5" data-id="element-6934" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-4" data-id="element-6935">
                <div data-id="element-6936">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6937">
                    Full Name
                  </label>
                  <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} placeholder="e.g. John Doe" data-id="element-6938" />
                </div>

                <div data-id="element-6939">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6940">
                    Email Address
                  </label>
                  <input type="email" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} placeholder="john@example.com" data-id="element-6941" />
                </div>

                <div data-id="element-6942">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6943">
                    Role
                  </label>
                  <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.role} onChange={e => setFormData({
                ...formData,
                role: e.target.value as any
              })} data-id="element-6944">
                    <option value="Viewer" data-id="element-6945">Viewer</option>
                    <option value="Operator" data-id="element-6946">Operator</option>
                    <option value="Team Manager" data-id="element-6947">Team Manager</option>
                    {currentUser.Role === 'Super Admin' && <option value="Super Admin" data-id="element-6948">Super Admin</option>}
                  </select>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-6949">
                    {formData.role === 'Viewer' && 'Read-only access to all data.'}
                    {formData.role === 'Operator' && 'Can manage content but not settings.'}
                    {formData.role === 'Team Manager' && 'Full access within assigned team.'}
                    {formData.role === 'Super Admin' && 'Full access across all teams.'}
                  </p>
                </div>

                <div data-id="element-6950">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6951">
                    Team
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={currentWorkspace.name} disabled data-id="element-6952" />
                </div>

                {availableRegions.length > 0 && <div data-id="element-6953">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-6954">
                      Region Access
                    </label>
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3 bg-gray-50" data-id="element-6955">
                      {availableRegions.map(region => <label key={region} className="flex items-center gap-2 cursor-pointer" data-id="element-6956">
                          <input type="checkbox" className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" checked={formData.regions.includes(region)} onChange={e => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        regions: [...formData.regions, region]
                      });
                    } else {
                      setFormData({
                        ...formData,
                        regions: formData.regions.filter(r => r !== region)
                      });
                    }
                  }} data-id="element-6957" />
                          <span className="text-sm text-gray-700" data-id="element-6958">
                            {region}
                          </span>
                        </label>)}
                    </div>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-6959">
                      Leave empty to grant access to all regions in the team.
                    </p>
                  </div>}

                <div className="pt-4 flex justify-end gap-3 border-t border-gray-100 mt-6" data-id="element-6960">
                  <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-6961">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-6962">
                    {isSaving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-6963" />}
                    {editingUser ? 'Save Changes' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      <ConfirmModal isOpen={!!userToDelete} title="Delete User" message={`Are you sure you want to delete ${userToDelete?.User_Name}?`} confirmLabel="Delete" variant="danger" onCancel={() => setUserToDelete(null)} onConfirm={confirmDelete} data-id="element-6964" />
    </div>;
}